import type { base as de } from '../de/base';

/** Texts of manifest, permissions, audit, workflow and notifications. Pages have their own file. */
export const base: typeof de = {
  name: 'Tenders',
  description:
    'Public tenders from Germany with an open deadline, work on marked tenders up to the submission, and a collection of proof.',
  nav: {
    tenders: 'Tenders',
    mine: 'My tenders',
    evidence: 'Proof',
    sources: 'Sources',
  },
  entities: { tender: 'Tender', evidence: 'Proof' },
  permissions: {
    tender: {
      read: {
        label: 'View tenders',
        description: 'Open tenders and marked tenders with their checklist.',
      },
      write: {
        label: 'Work on tenders',
        description:
          'Mark and enter tenders, add people, change the status, keep checklist and documents.',
      },
      comment: {
        label: 'Comment on tenders',
        description: 'Write comments on marked tenders and mention people.',
      },
      remind: {
        label: 'Reminders on tenders',
        description: 'Set own reminders on marked tenders.',
      },
      tasks: {
        label: 'Tasks on tenders',
        description: 'See, create and complete tasks on marked tenders.',
      },
      summarize: {
        label: 'Create AI summary',
        description: 'Have the AI summarise a marked tender and list the documents needed.',
      },
    },
    evidence: {
      read: {
        label: 'View proof',
        description: 'Open the collection of proof and download proof.',
      },
      write: {
        label: 'Keep proof',
        description: 'Create, change and delete proof and upload its files.',
      },
    },
    settings: {
      manage: {
        label: 'Set up tenders',
        description: 'Switch sources on and off, start the import and change settings.',
      },
    },
  },
  roles: {
    reader: 'Tenders: read',
    editor: 'Tenders: edit',
    manager: 'Tenders: manage',
  },
  settings: {
    sourceOev: {
      label: 'oeffentlichevergabe.de',
      description:
        'All notices of the federal government, states and municipalities from the public procurement data service.',
    },
    sourceNrw: {
      label: 'Procurement portals NRW',
      description:
        'Open data of the procurement portals in North Rhine-Westphalia, national procedures below the EU thresholds included.',
    },
    sourceTed: {
      label: 'TED (EU Official Journal)',
      description:
        'EU-wide notices with a buyer or place of performance in Germany from the Official Journal of the EU.',
    },
    sourceBerlin: {
      label: 'Berlin procurement platform',
      description: 'Latest notices of the State of Berlin, national procedures included.',
    },
    sourceVkberlin: {
      label: 'Vergabekooperation Berlin',
      description:
        'Tenders of BVG, Berliner Wasserbetriebe, BSR, Messe Berlin, Forschungsverbund Berlin and others.',
    },
    sourceBund: {
      label: 'service.bund.de',
      description:
        'Tenders of the federal government, states and municipalities from the federal portal.',
    },
    feedbackIntervalDays: {
      label: 'Days until the next check for an answer',
      description:
        'After the submission the app reminds the person responsible at this interval to check with the buyer for an answer.',
    },
  },
  states: {
    review: 'Review',
    preparing: 'Preparing',
    submitted: 'Submitted',
    waiting: 'Waiting for answer',
    won: 'Won',
    lost: 'Not won',
    dropped: 'Dropped',
  },
  transitions: {
    prepare: 'Prepare bid',
    submit: 'Submitted',
    wait: 'Wait for answer',
    win: 'Contract awarded',
    lose: 'Not won',
    drop: 'Do not bid',
  },
  ai: {
    summaryFiles: 'Summary with tender documents',
    summary: 'Summary of a tender',
  },
  notifications: {
    deadlineSoon: 'Tender deadline in {{days}} days: {{title}}',
    participationSoon: 'Participation deadline in {{days}} days: {{title}}',
    deadlineToday: 'Deadline ends today: {{title}}',
    deadlineChanged: 'Deadline changed: {{title}}',
    feedbackDue: 'Check for an answer: {{title}}',
    evidenceExpiring: 'Proof expires on {{date}}: {{title}}',
    evidenceExpired: 'Proof expired: {{title}}',
  },
  audit: {
    tender: {
      mark: 'Tender marked',
      unmark: 'Mark taken back',
      create: 'Tender entered',
      update: 'Tender changed',
      delete: 'Tender deleted',
      owner: 'Person responsible changed',
      'member-add': 'Person added',
      'member-remove': 'Person removed',
      'feedback-checked': 'Answer checked',
      'documents-fetch': 'Tender documents loaded',
      project: 'Project linked',
    },
    checklist: {
      add: 'Points added to the checklist',
      update: 'Point of the checklist changed',
      remove: 'Point of the checklist deleted',
      link: 'Proof linked',
      unlink: 'Proof unlinked',
    },
    evidence: {
      create: 'Proof created',
      update: 'Proof changed',
      delete: 'Proof deleted',
    },
  },
  errors: {
    title: 'Please enter a title (at most 1000 characters).',
    description: 'The description is too long.',
    buyer: 'Please shorten the contracting authority.',
    place: 'Please shorten the place.',
    deadline: 'Please enter a valid deadline.',
    url: 'Please enter a valid address starting with https://.',
    owner: 'This person does not exist or is not active.',
    member: 'This person does not exist or is not active.',
    project: 'This project does not exist or you cannot see it.',
    itemTitle: 'Please enter a title (at most 300 characters).',
    evidenceTitle: 'Please enter a title (at most 300 characters).',
    category: 'The category is too long.',
    date: 'Please enter a valid date.',
    responsible: 'This person does not exist or is not active.',
  },
  guides: {
    tenders: {
      title: 'Tenders',
      summary:
        'Find tenders, mark them, work on them together and keep track of the answer after the submission.',
    },
  },
};
